import { UploadService } from "@/services/APIs";
import {
  AuthenticatedMediaImage,
  AuthenticatedMediaVideo,
  collectAttachmentMediaPaths,
  openAuthenticatedMediaPreview,
} from "@/components/common/AuthenticatedMediaImage";
import { prefetchVideoBlob, unwrapFileRecord } from "@sd/lib/attachmentMediaUrl";
import {
  UploadOutlined,
  DeleteOutlined,
  EyeOutlined,
  PaperClipOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileOutlined,
} from "@ant-design/icons";
import { notify } from "@/components/common/Notification";
import {
  IMAGE_UPLOAD_ACCEPT,
  IMAGE_UPLOAD_ERROR_TITLE,
  IMAGE_UPLOAD_HINT,
  IMAGE_UPLOAD_HINT_MIXED,
  IMAGE_UPLOAD_VALIDATION_MESSAGE,
  isAllowedImageUpload,
  pickImageUploadFiles,
  schemaAllowsNonImageUploads,
  validateImageUploadFile,
} from "@/lib/imageUploadValidation";
import { Button, Modal } from "antd";
import type { UploadProps } from "antd/es/upload";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

export const CustomFileUpload = (
  props: UploadProps & {
    action: string;
    value?: File[] | string[];
    onChange?: (value: File[] | string[]) => void;
    defaultFileList?: File[];
  }
) => {
  const { id } = useParams();
  const editMode = !!id;
  const { value = [], onChange, accept: schemaAccept } = props;
  const allowsNonImageFiles = useMemo(
    () => schemaAllowsNonImageUploads(schemaAccept),
    [schemaAccept]
  );
  const pickerAccept = allowsNonImageFiles
    ? (schemaAccept ?? undefined)
    : IMAGE_UPLOAD_ACCEPT;
  const fileInputRef = useRef<HTMLInputElement>(null);
  // const parsedValue = typeof value === "string" ? JSON.parse(value) : value;
  // const editMode = !!id;

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<any>();
  const [previewFile, setPreviewFile] = useState<{
    url: string;
    fallbackPaths?: string[];
    name: string;
    isImage: boolean;
    isVideo: boolean;
  } | null>(null);

  const isImageFile = (name: string) => {
    const ext = name.split(".").pop()?.toLowerCase() ?? "";
    return ["jpg", "jpeg", "png", "gif", "webp", "avif"].includes(ext);
  };

  const resolveFileMime = (file: Record<string, unknown>) =>
    String(
      file?.file_type ??
        file?.mime_type ??
        file?.content_type ??
        file?.mimetype ??
        file?.type ??
        ""
    );

  const isVideoFile = (name: string, fileType?: string) => {
    const ext = name.split(".").pop()?.toLowerCase() ?? "";
    const mime = (fileType ?? "").toLowerCase();
    if (mime.startsWith("video/")) {
      return true;
    }
    if (
      (mime.includes("octet-stream") || !mime.trim()) &&
      ["webm", "mp4", "ogg", "mov", "avi"].includes(ext)
    ) {
      return true;
    }
    return ["webm", "mp4", "ogg", "mov", "avi"].includes(ext);
  };

  const warmVideoCache = (record: Record<string, unknown>) => {
    const fileName =
      String(
        record.file_original_name ??
          record.original_name ??
          record.filename ??
          record.name ??
          record.file_name ??
          ""
      ) || "recording.webm";
    if (!isVideoFile(fileName, resolveFileMime(record))) {
      return;
    }
    const fileId = String(record.id ?? record.pk ?? "").trim();
    if (!fileId) {
      return;
    }
    prefetchVideoBlob(fileId, fileName, collectAttachmentMediaPaths(record));
  };

  useEffect(() => {
    if (!value) return;

    const isUuid = (v: unknown) =>
      typeof v === "string" &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v.trim());

    // Collect all UUIDs to fetch, regardless of format
    const uuidsToFetch: string[] = [];

    if (isUuid(value)) {
      // Format: "6252bcb4-671e-4f4e-957a-0cbad06f531e"
      uuidsToFetch.push((value as string).trim());
    } else if (typeof value === "string") {
      // Format: "[\"987579d8-...\"]" — JSON string array
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          if (parsed.length === 0) { setUploadedFiles([]); return; }
          parsed.forEach((v: unknown) => { if (isUuid(v)) uuidsToFetch.push((v as string).trim()); });
        } else if (isUuid(parsed)) {
          uuidsToFetch.push(String(parsed).trim());
        }
      } catch {
        setUploadedFiles([]);
        return;
      }
    } else if (Array.isArray(value)) {
      if (value.length === 0) { setUploadedFiles([]); return; }
      value.forEach((v: unknown) => {
        if (isUuid(v)) uuidsToFetch.push((v as string).trim());
        // Already a file object (just uploaded) — keep as-is
      });
      // If none are UUIDs, they're already file objects
      if (uuidsToFetch.length === 0) {
        (value as unknown[]).forEach(item => {
          if (item && typeof item === "object") {
            const record =
              unwrapFileRecord(item as Record<string, unknown>) ??
              (item as Record<string, unknown>);
            warmVideoCache(record);
          }
        });
        setUploadedFiles(value);
        return;
      }
    }

    if (uuidsToFetch.length > 0) {
      Promise.all(
        uuidsToFetch.map(id =>
          UploadService.getFileById({ id }).catch(() => null)
        )
      ).then(results => {
        const records = results
          .map(r => unwrapFileRecord(r as Record<string, unknown>))
          .filter((r): r is Record<string, unknown> => Boolean(r));
        records.forEach(warmVideoCache);
        setUploadedFiles(records);
      });
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileSelect = async (file: File) => {
    if (!allowsNonImageFiles) {
      if (!isAllowedImageUpload(file)) {
        notify.error(IMAGE_UPLOAD_ERROR_TITLE, IMAGE_UPLOAD_VALIDATION_MESSAGE);
        return false;
      }
    } else if (!validateImageUploadFile(file)) {
      notify.error(IMAGE_UPLOAD_ERROR_TITLE, IMAGE_UPLOAD_VALIDATION_MESSAGE);
      return false;
    }

    const newFiles = [...selectedFiles, file];
    setSelectedFiles(newFiles);
    try {
      const response = await UploadService.uploadFiles({
        folderName: "uploads",
        files: newFiles,
      });

      // API may return a single object OR an array
      const uploadedItems: unknown[] = Array.isArray(response)
        ? response
        : response && typeof response === "object"
        ? [response]
        : [];

      if (uploadedItems.length > 0) {
        const safeValue = Array.isArray(value) ? value : [];
        const newValue = [...safeValue, ...uploadedItems];
        onChange?.(newValue);
        const records = newValue
          .map(item =>
            typeof item === "object" && item !== null
              ? unwrapFileRecord(item as Record<string, unknown>) ?? (item as Record<string, unknown>)
              : null
          )
          .filter((r): r is Record<string, unknown> => Boolean(r));
        records.forEach(warmVideoCache);
        setUploadedFiles(records);
      }
    } catch (error) {
      console.error("Upload failed:", error);
    }
    return false;
  };

  const handleRemove = (file: any) => {
    const fileToRemove = file.file_path ?? file.url ?? file.name;
    const filteredFiles = uploadedFiles.filter(
      (f: any) => (f.file_path ?? f.url ?? f.name) !== fileToRemove
    );

    setUploadedFiles(filteredFiles);
    setSelectedFiles([]);
    onChange?.(filteredFiles);
  };

  const openFilePicker = async () => {
    if (!allowsNonImageFiles) {
      const files = await pickImageUploadFiles(false, fileInputRef.current);
      for (const file of files) {
        await handleFileSelect(file);
      }
      return;
    }
    fileInputRef.current?.click();
  };

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) {
      await handleFileSelect(file);
    }
  };

  const handlePreview = (file: any) => {
    const mediaPaths = collectAttachmentMediaPaths(file);
    const url = mediaPaths[0];
    if (!url) {
      return;
    }
    void openAuthenticatedMediaPreview(String(url), mediaPaths.slice(1));
  };

  const getFileIcon = (name: string) => {
    const ext = name.split(".").pop()?.toLowerCase() ?? "";
    if (["jpg", "jpeg", "png", "gif", "webp", "svg", "avif"].includes(ext))
      return <FileImageOutlined style={{ color: "#6366f1", fontSize: 22 }} />;
    if (ext === "pdf")
      return <FilePdfOutlined style={{ color: "#ef4444", fontSize: 22 }} />;
    if (["doc", "docx", "txt", "csv", "xls", "xlsx"].includes(ext))
      return <FileTextOutlined style={{ color: "#0ea5e9", fontSize: 22 }} />;
    return <FileOutlined style={{ color: "#64748b", fontSize: 22 }} />;
  };

  const showUploadButton =
    !uploadedFiles ||
    uploadedFiles.length === 0 ||
    uploadedFiles.every((f: any) => f === null);

  return (
    <div className="space-y-3">
      {/* Existing attached files */}
      {(uploadedFiles ?? []).filter(Boolean).map((file: any, index: number) => {
        const record = unwrapFileRecord(file) ?? file;
        const mediaPaths = collectAttachmentMediaPaths(record);
        const filePath = mediaPaths[0] ?? "";
        const fileId = String(record?.id ?? record?.pk ?? "").trim();
        // Extract filename: prefer API-provided name fields; fall back to the absolute
        // file_path URL (e.g. ".../recording.webm") before using the download path segment.
        const rawRecordPath = String(record?.file_path ?? record?.url ?? "").trim();
        const nameFromRecordPath = rawRecordPath ? rawRecordPath.split("/").pop()?.split("?")[0] ?? "" : "";
        const fileName =
          record?.file_original_name ??
          record?.original_name ??
          record?.filename ??
          record?.name ??
          record?.file_name ??
          (nameFromRecordPath && nameFromRecordPath.includes(".") ? nameFromRecordPath : null) ??
          (filePath ? filePath.split("/").pop() : null) ??
          "attachment";
        const isImage = isImageFile(fileName);
        const isVideo = !isImage && isVideoFile(fileName, resolveFileMime(record));

        return (
          <div
            key={record?.id ?? index}
            style={{
              borderRadius: 10,
              border: "1.5px solid #e2e8f0",
              background: "#f8fafc",
              boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
              overflow: "hidden",
            }}
          >
            {/* Full image preview inside card */}
            {isImage && filePath && (
              <AuthenticatedMediaImage
                filePath={String(filePath)}
                fallbackPaths={mediaPaths.slice(1)}
                alt={fileName}
                style={{
                  width: "100%",
                  maxHeight: 220,
                  objectFit: "contain",
                  background: "#fff",
                  display: "block",
                  borderBottom: "1px solid #e2e8f0",
                }}
              />
            )}

            {/* Inline video preview inside card */}
            {isVideo && (rawRecordPath || filePath || fileId) && (
              <video
                src={
                  /^https?:\/\//i.test(rawRecordPath)
                    ? rawRecordPath
                    : filePath || fileId
                }
                controls
                preload="metadata"
                playsInline
                style={{
                  width: "100%",
                  maxHeight: 220,
                  background: "#000",
                  display: "block",
                  borderBottom: "1px solid #e2e8f0",
                }}
              />
            )}

            {/* File info row */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 14px",
              }}
            >
              {/* Icon for non-images */}
              {!isImage && (
                <div
                  style={{
                    width: 40,
                    height: 40,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#f1f5f9",
                    borderRadius: 8,
                    border: "1px solid #e2e8f0",
                    flexShrink: 0,
                  }}
                >
                  {getFileIcon(fileName)}
                </div>
              )}

              {/* File info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 13,
                    color: "#1e293b",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={fileName}
                >
                  <PaperClipOutlined style={{ marginRight: 5, color: "#94a3b8" }} />
                  {fileName}
                </div>
                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                  Attached file
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                {filePath && (
                  <button
                    type="button"
                    title="Preview"
                    onClick={() =>
                      setPreviewFile({
                        url: String(filePath),
                        fallbackPaths: mediaPaths.slice(1),
                        name: fileName,
                        isImage,
                        isVideo,
                      })
                    }
                    style={{
                      background: "#e0f2fe",
                      border: "none",
                      borderRadius: 7,
                      padding: "5px 9px",
                      cursor: "pointer",
                      color: "#0284c7",
                      fontSize: 14,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <EyeOutlined />
                  </button>
                )}

                <button
                  type="button"
                  title="Remove"
                  onClick={() => handleRemove({ file_path: filePath, url: filePath, name: fileName })}
                  style={{
                    background: "#fee2e2",
                    border: "none",
                    borderRadius: 7,
                    padding: "5px 9px",
                    cursor: "pointer",
                    color: "#dc2626",
                    fontSize: 14,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <DeleteOutlined />
                </button>
              </div>
            </div>
          </div>
        );
      })}

      {/* Upload button — only show if no file yet */}
      {/* Preview Modal */}
      <Modal
        open={!!previewFile}
        onCancel={() => setPreviewFile(null)}
        footer={null}
        centered
        title={previewFile?.name ?? "Preview"}
        width={previewFile?.isImage ? 640 : previewFile?.isVideo ? 720 : 520}
        destroyOnClose
      >
        {previewFile?.isImage ? (
          <AuthenticatedMediaImage
            filePath={previewFile.url}
            fallbackPaths={previewFile.fallbackPaths}
            alt={previewFile.name}
            style={{ width: "100%", borderRadius: 8 }}
          />
        ) : previewFile?.isVideo ? (
          <AuthenticatedMediaVideo
            filePath={previewFile.url}
            fallbackPaths={previewFile.fallbackPaths}
            filenameHint={previewFile.name}
            style={{ width: "100%", borderRadius: 8, background: "#000", display: "block", maxHeight: 400 }}
          />
        ) : (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <FileOutlined style={{ fontSize: 48, color: "#64748b", marginBottom: 16 }} />
            <div style={{ color: "#475569", fontSize: 14 }}>
              {previewFile?.name}
            </div>
          </div>
        )}
      </Modal>

      {showUploadButton && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={pickerAccept}
            onChange={handleInputChange}
            style={{ display: "none" }}
            aria-hidden
          />
          <Button
            type="button"
            icon={<UploadOutlined />}
            onClick={() => void openFilePicker()}
            style={{
              borderRadius: 8,
              borderStyle: "dashed",
              width: "100%",
              height: 44,
              color: "#64748b",
              borderColor: "#cbd5e1",
              background: "#f8fafc",
            }}
          >
            {allowsNonImageFiles ? "Click to upload file" : "Click to upload image"}
          </Button>
        </>
      )}
      {showUploadButton && (
        <p className="text-xs text-gray-500">
          {allowsNonImageFiles ? IMAGE_UPLOAD_HINT_MIXED : IMAGE_UPLOAD_HINT}
        </p>
      )}
    </div>
  );
};
